import { FC } from 'react';
import { ExclusionOrderModalProps } from './exclusionOrderModal.type';
import { ExclusionOrderModel } from '../../models/exclusionOrderModel';
import ModalComponent from '../modal/modal';
import { useTranslation } from 'react-i18next';
import styles from './exclusionOrderModal.module.css';

const ExclusionOrderModal: FC<ExclusionOrderModalProps> = (props) => {
  const { t } = useTranslation();

  if (!props.exclusionOrder) {
    return null;
  }

  const order: ExclusionOrderModel = props.exclusionOrder;

  return (
    <>
      {props.isOpen && <div className={styles.exclusionModalBackdrop} />}
      <ModalComponent
        open={props.isOpen}
        onToggle={(open) => {
          if (!open) props.onClose();
        }}
        title={`${t('exclusionOrder.title')} — #${order.reportNo}`}
        onClose={props.onClose}
        isShowClose
        showAction
        footerHidden
        centered
        dialogClassName="w-[90vw] sm:w-[80vw] md:w-[36rem] lg:w-[44rem]"
      >
        <div className="p-4 overflow-y-auto max-h-[70vh]">
          {/* General Info */}
          <section className={styles.section}>
            <div className={styles.sectionLabel}>
              {t('exclusionOrder.generalInfo')}
            </div>
            <div className={styles.sectionContent}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.reportNo')}
                  </span>
                  <p>{order.reportNo}</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.exclusionType')}
                  </span>
                  <p>{t(order.exclusionTypeKey)}</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.venue')}
                  </span>
                  <p>{order.venueName}</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.requestedOn')}
                  </span>
                  <p>{order.createdAtFormatted}</p>
                </div>
              </div>
            </div>
          </section>

          {/* POI Photos */}
          {(props.faceImage || props.fullImage) && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t('exclusionOrder.poiPhotos')}
              </div>
              <div className={styles.sectionContent}>
                <div className="flex gap-3">
                  {props.faceImage && (
                    <img
                      src={props.faceImage}
                      alt="POI face"
                      className="w-24 h-28 object-cover rounded"
                    />
                  )}
                  {props.fullImage && (
                    <img
                      src={props.fullImage}
                      alt="POI full"
                      className="w-24 h-28 object-cover rounded"
                    />
                  )}
                </div>
              </div>
            </section>
          )}

          {/* POI Details */}
          <section className={styles.section}>
            <div className={styles.sectionLabel}>
              {t('exclusionOrder.poiDetails')}
            </div>
            <div className={styles.sectionContent}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.poiNo')}
                  </span>
                  <p>-</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.gender')}
                  </span>
                  <p>-</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.name')}
                  </span>
                  <p>{order.playerFullName}</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.dateOfBirth')}
                  </span>
                  <p>{order.playerDob}</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.description')}
                  </span>
                  <p>-</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.age')}
                  </span>
                  <p>-</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.address')}
                  </span>
                  <p>{order.playerAddress}</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.ethnicity')}
                  </span>
                  <p>-</p>
                </div>
              </div>
            </div>
          </section>

          {/* Exclusion Period */}
          <section className={styles.section}>
            <div className={styles.sectionLabel}>
              {t('exclusionOrder.exclusionPeriod')}
            </div>
            <div className={styles.sectionContent}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.from')}
                  </span>
                  <p>{order.startDateFormatted}</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.expiresIn')}
                  </span>
                  <p>
                    {order.isActive
                      ? `${order.expiresInDisplay} ${t('exclusionOrder.days')}`
                      : t(order.expiresInDisplay)}
                  </p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.until')}
                  </span>
                  <p>{order.durationLabel}</p>
                </div>
                <div>
                  <span className="text-indigo-600">
                    {t('exclusionOrder.breaches')}
                  </span>
                  <p>{order.breachCount}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Notes */}
          {order.reason && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t('exclusionOrder.notes')}
              </div>
              <div className={styles.sectionContent}>
                <p className="text-sm">{order.reason}</p>
              </div>
            </section>
          )}

          {/* Re-entry Conditions */}
          {order.reentryConditions && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t('exclusionOrder.reentryConditions')}
              </div>
              <div className={styles.sectionContent}>
                <p className="text-sm">{order.reentryConditions}</p>
              </div>
            </section>
          )}

          {/* Exclusion Issued By */}
          <section className={styles.section}>
            <div className={styles.sectionLabel}>
              {t('exclusionOrder.issuedBy')}
            </div>
            <div className={styles.sectionContent}>
              <p className="text-sm">{order.issuedByFullName}</p>
              <p className="text-sm text-gray-400">
                {order.createdAtFormatted}
              </p>
            </div>
          </section>

          {/* Gambler Consent */}
          {order.gamblerConsent && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t('exclusionOrder.gambler')}
              </div>
              <div className={styles.sectionContent}>
                <p className="text-sm">{t(order.gamblerConsentLabel)}</p>
                {order.gamblerSignedAtFormatted && (
                  <p className="text-sm text-gray-400">
                    {order.gamblerSignedAtFormatted}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>
      </ModalComponent>
    </>
  );
};

export default ExclusionOrderModal;
